// Problem — three big buttons for how bad it is, an optional job, a
// sentence, Send. Goes through the queue like everything else, so a safety
// report typed with no signal is not lost; it is sent the moment there is one.

import React, { useState } from 'react';
import { FwButton, Notice, FwField, FwSelect, FwTextarea, SectionLabel } from '../../../ui/fw';
import { describeError } from '../../shared/http';
import { useToday } from '../queries';
import { reportProblem } from '../actions';
import { plural } from '../fmt';

/**
 * The three severities, and what the CHOSEN one looks like.
 *
 * `variant` is the SELECTED appearance, not the resting one. Every button on
 * this row used to wear its variant all the time — Note outlined, Blocked
 * amber, Safety red, at rest and when picked alike — so the only thing that
 * said which one you had actually tapped was a 3px focus ring, and the screen
 * looked the same whether you had chosen anything or not. That is the owner's
 * "statuses indistinguishable" one screen over, and it is worse here: the
 * thing being chosen is how urgent a problem is.
 *
 * Now the resting state is a plain outline and the chosen one is solid, with a
 * tick in it and `aria-pressed="true"`. Three signals — weight, a glyph, and
 * the accessible state — so it survives sunlight and a red-green crew member,
 * neither of which a colour alone does (docs/APP_REBUILD.md §3.3).
 *
 * Note and Blocked share the signal colour on purpose: there are three
 * severities and two meanings of loud, and spending the danger colour on
 * anything but Safety is what makes Safety stop meaning anything.
 */
export const SEVERITIES = [
  { key: 'note', label: 'Note', variant: 'primary', hint: 'Something the office should know.' },
  { key: 'blocked', label: 'Blocked', variant: 'primary', hint: 'You cannot finish this job.' },
  { key: 'safety', label: 'Safety', variant: 'stop', hint: 'Someone could get hurt.' },
];

export default function Problem() {
  const today = useToday();
  const jobs = (today.data && today.data.jobs) || [];
  // A picker that failed to load looks like DATA — fewer options, nothing
  // said. Today and Job both replace their whole body with the server's
  // sentence when this request is refused; this screen just rendered a job
  // list of one ("No particular job"), so a crew member reporting something
  // that could hurt somebody reads it as "my job is not on file", picks
  // nothing, and the report reaches the office detached from the job it is
  // about. Say it failed and leave the control usable — the text box is the
  // fallback and always was.
  const jobsUnknown = !!today.error && !today.data;
  const [severity, setSeverity] = useState('note');
  const [jobId, setJobId] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const chosen = SEVERITIES.find((s) => s.key === severity) || SEVERITIES[0];

  const send = async () => {
    const body = text.trim();
    if (body.length < 3) { setError('Say a little more — at least a few words.'); return; }
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const out = await reportProblem({ job_id: jobId ? Number(jobId) : null, text: body, severity });
      if (out.sent) {
        const n = out.result && out.result.notified;
        setDone(n != null ? `Sent to ${plural(n, 'person', 'people')}.` : 'Sent.');
      } else {
        setDone('Saved on this phone — it will send when you have signal.');
      }
      setText('');
      setJobId('');
      setSeverity('note');
    } catch (e) {
      setError(describeError(e, 'That did not go through'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {done ? <Notice tone="ok" onDismiss={() => setDone(null)}>{done}</Notice> : null}
      {error ? <Notice tone="stop" onDismiss={() => setError(null)}>{error}</Notice> : null}
      <div className="fd-form">
        <div className="fd-section">
          <SectionLabel>How bad is it?</SectionLabel>
          <div className="fd-sev" role="group" aria-label="Severity">
            {SEVERITIES.map((s) => {
              const picked = severity === s.key;
              return (
                <FwButton
                  key={s.key}
                  size="lg"
                  full
                  variant={picked ? s.variant : 'ghost'}
                  icon={picked ? 'check' : undefined}
                  aria-pressed={picked ? 'true' : 'false'}
                  onClick={() => setSeverity(s.key)}
                  data-severity={s.key}
                >
                  {s.label}
                </FwButton>
              );
            })}
          </div>
          <div className="fd-hint">{chosen.hint}</div>
        </div>
        {jobsUnknown ? (
          <Notice tone="warn" title="Your job list did not load">
            {describeError(today.error, 'Today’s jobs could not be fetched.')} Say which job in the box below — the report still sends.
          </Notice>
        ) : null}
        <FwField label="Job" htmlFor="fd-problem-job" hint={jobsUnknown ? 'Not available right now.' : 'Optional.'}>
          <FwSelect value={jobId} onChange={(e) => setJobId(e.target.value)}>
            <option value="">No particular job</option>
            {jobs.map((j) => (
              <option key={j.id} value={String(j.id)}>{j.title}{j.property_name ? ` - ${j.property_name}` : ''}</option>
            ))}
          </FwSelect>
        </FwField>
        <FwField label="What happened" htmlFor="fd-problem-text" required>
          <FwTextarea value={text} onChange={(e) => setText(e.target.value)} rows={5} placeholder="Mower blade bent on a rock" />
        </FwField>
        <FwButton size="lg" full variant={severity === 'safety' ? 'stop' : 'primary'} onClick={send} loading={busy} disabled={text.trim().length === 0}>
          Send
        </FwButton>
      </div>
    </>
  );
}
